import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const swatches = [
  { name: "background", value: "var(--background)", hex: "#faf6ed" },
  { name: "foreground / ink", value: "var(--foreground)", hex: "#1a2922" },
  { name: "primary (forest)", value: "var(--primary)", hex: "#1f3a2e" },
  { name: "accent (orange)", value: "var(--accent)", hex: "#e08e45" },
  { name: "secondary", value: "var(--secondary)", hex: "#efe7d4" },
  { name: "muted", value: "var(--muted)", hex: "#ede5d2" },
  { name: "success", value: "var(--success)", hex: "#5a9a4a" },
  { name: "warning", value: "var(--warning)", hex: "#d4a017" },
  { name: "destructive", value: "var(--destructive)", hex: "#c44545" },
  { name: "info", value: "var(--info)", hex: "#5a8ec4" },
];

function Section({ title, children }) {
  return (
    <div className="rounded-3xl bg-card border border-border p-6 shadow-soft">
      <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-4">{title}</div>
      {children}
    </div>
  );
}

function Inline({ icon: Icon, bg, text, title, desc }) {
  return (
    <div className={`flex gap-4 items-start rounded-2xl p-5 ${bg}`}>
      <Icon className={`h-5 w-5 ${text} shrink-0 mt-0.5`} />
      <div>
        <div className={`font-display text-lg ${text}`}>{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function Badge({ color, children }) {
  const map = {
    success: "bg-success/10 text-success",
    muted: "bg-muted text-muted-foreground",
    accent: "bg-accent/10 text-accent",
    destructive: "bg-destructive/10 text-destructive",
    info: "bg-info/10 text-info",
    warning: "bg-warning/10 text-warning",
  };
  return <span className={`inline-flex text-xs px-2.5 py-1 rounded-full ${map[color]}`}>{children}</span>;
}

export default function StyleGuidePage() {
  const [tab, setTab] = useState("colors");

  return (
    <PageShell>
      <PageHero eyebrow="Reference" title="The Smart Farming" highlight="style guide." description="Tokens, components and patterns that keep every page feeling like the same publication." />
      <PageSection variant="content">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap gap-1 p-1 rounded-full bg-secondary/60 w-fit mb-10">
            {["colors", "type", "buttons", "cards", "alerts", "forms"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-5 py-2 rounded-full text-sm capitalize transition-all ${tab === t ? "bg-card shadow-sm" : "text-muted-foreground"}`} type="button">{t}</button>
            ))}
          </div>

          {tab === "colors" && (
            <div>
              <h2 className="font-display text-4xl mb-6">Color tokens</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {swatches.map((s) => (
                  <div key={s.name} className="rounded-2xl border border-border overflow-hidden bg-card shadow-soft">
                    <div className="h-28" style={{ background: s.value }} />
                    <div className="p-4">
                      <div className="font-display text-lg">{s.name}</div>
                      <div className="font-mono text-xs text-muted-foreground mt-1">{s.value}</div>
                      <div className="font-mono text-xs text-muted-foreground">{s.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "type" && (
            <div className="space-y-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-2">Display · Instrument Serif</div>
                <h1 className="font-display text-7xl">Cultivate by data.</h1>
                <h2 className="font-display text-5xl mt-4">A second display headline.</h2>
                <h3 className="font-display text-3xl mt-3">Section heading 03</h3>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-2">Body · Work Sans</div>
                <p className="text-lg max-w-2xl">Larger body for editorial intros and intro paragraphs that deserve room to breathe.</p>
                <p className="text-base max-w-2xl mt-3 text-muted-foreground">Standard body copy. Comfortable measure, generous leading. Used everywhere by default for readable runs of text.</p>
                <p className="text-sm mt-3 text-muted-foreground">Smaller secondary text for metadata, captions and timestamps.</p>
                <p className="text-xs uppercase tracking-widest mt-3 text-muted-foreground">Eyebrow / label style</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-2">Mono · JetBrains Mono</div>
                <code className="font-mono text-sm bg-secondary/50 px-3 py-1 rounded">device_003 · v1.4.2</code>
              </div>
            </div>
          )}

          {tab === "buttons" && (
            <div className="space-y-8">
              <Section title="Variants">
                <div className="flex flex-wrap gap-3">
                  <Button variant="default">Default</Button>
                  <Button variant="accent">Accent</Button>
                  <Button variant="hero">Hero</Button>
                  <Button variant="cream">Cream</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="outlineAccent">Outline accent</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button variant="link">Link style</Button>
                </div>
              </Section>
              <Section title="Sizes">
                <div className="flex flex-wrap gap-3 items-center">
                  <Button size="sm">Small</Button>
                  <Button>Default</Button>
                  <Button size="lg">Large</Button>
                  <Button size="xl">Extra large</Button>
                </div>
              </Section>
              <Section title="States">
                <div className="flex flex-wrap gap-3">
                  <Button variant="accent">Default</Button>
                  <Button variant="accent" disabled>Disabled</Button>
                  <Button variant="accent" className="animate-[pulse-glow_2.4s_ease-in-out_infinite]">With glow</Button>
                </div>
              </Section>
            </div>
          )}

          {tab === "cards" && (
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-3xl bg-card border border-border p-6 shadow-soft"><div className="text-xs text-muted-foreground">Default card</div><h3 className="font-display text-2xl mt-2">Standard card</h3></div>
              <div className="rounded-3xl glass p-6 shadow-soft"><div className="text-xs text-muted-foreground">Glass card</div><h3 className="font-display text-2xl mt-2">Glassmorphism</h3></div>
              <div className="rounded-3xl p-6 text-accent-foreground shadow-elevated" style={{ background: "var(--gradient-warm)" }}><div className="text-xs opacity-80">Gradient card</div><h3 className="font-display text-2xl mt-2">Warm gradient</h3></div>
              <div className="rounded-3xl bg-primary text-cream p-6 shadow-soft"><div className="text-xs text-cream/70">Solid card</div><h3 className="font-display text-2xl mt-2">Forest solid</h3></div>
            </div>
          )}

          {tab === "alerts" && (
            <div className="space-y-6">
              <Section title="Inline alerts">
                <div className="space-y-3">
                  <Inline icon={CheckCircle2} bg="bg-success/10" text="text-success" title="Saved successfully" desc="Your changes have been written to the device." />
                  <Inline icon={Info} bg="bg-info/10" text="text-info" title="Heads up" desc="A firmware update will be applied tonight at 2 AM." />
                  <Inline icon={AlertTriangle} bg="bg-warning/10" text="text-warning" title="Battery getting low" desc="Field Sensor B is at 12%. Replace within 48 hours." />
                  <Inline icon={X} bg="bg-destructive/10" text="text-destructive" title="Sync failed" desc="Could not push Wi-Fi credentials. Check connection." />
                </div>
              </Section>
              <Section title="Toast notifications (sonner)">
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => toast("Plain message")}>Plain</Button>
                  <Button variant="outline" onClick={() => toast.success("Saved", { description: "All changes synced." })}>Success</Button>
                  <Button variant="outline" onClick={() => toast.info("Update available")}>Info</Button>
                  <Button variant="outline" onClick={() => toast.warning("Battery low")}>Warning</Button>
                  <Button variant="outline" onClick={() => toast.error("Failed to connect")}>Error</Button>
                </div>
              </Section>
              <Section title="Badge styles">
                <div className="flex flex-wrap gap-2">
                  <Badge color="success">Online</Badge>
                  <Badge color="muted">Offline</Badge>
                  <Badge color="accent">Live</Badge>
                  <Badge color="destructive">Critical</Badge>
                  <Badge color="info">Beta</Badge>
                  <Badge color="warning">Battery low</Badge>
                </div>
              </Section>
            </div>
          )}

          {tab === "forms" && (
            <div className="space-y-6 max-w-xl">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Text input</label>
                <input placeholder="Type here…" className="mt-2 w-full h-12 px-5 rounded-full bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Textarea</label>
                <textarea rows={4} placeholder="Multi-line input…" className="mt-2 w-full rounded-2xl px-5 py-3 bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent resize-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Slider</label>
                <input type="range" defaultValue={42} className="mt-3 w-full accent-accent" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked className="accent-accent" /> Checkbox</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="r" defaultChecked className="accent-accent" /> Radio A</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="r" className="accent-accent" /> Radio B</label>
              </div>
            </div>
          )}
        </div>
      </PageSection>
    </PageShell>
  );
}

